/**
 * Label and icon for each settings tab (#2425).
 *
 * Both the home page and the sub-page heading need these, so they live in one
 * place rather than being spelled out again in the JSX of each. The grouping
 * itself is in `settingsTabs.ts`, which stays free of JSX so it can be unit
 * tested without a renderer.
 */

import type { ReactNode } from 'react';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import CategoryIcon from '@mui/icons-material/Category';
import ChatIcon from '@mui/icons-material/Chat';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HandymanIcon from '@mui/icons-material/Handyman';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import LockClockIcon from '@mui/icons-material/LockClock';
import MapIcon from '@mui/icons-material/Map';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NumbersIcon from '@mui/icons-material/Numbers';
import PaletteIcon from '@mui/icons-material/Palette';
import PaymentsIcon from '@mui/icons-material/Payments';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import RouteIcon from '@mui/icons-material/Route';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SchoolIcon from '@mui/icons-material/School';
import SmsIcon from '@mui/icons-material/Sms';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import StoreIcon from '@mui/icons-material/Store';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import type { SettingsGroup, SettingsTab } from './settingsTabs';

interface TabMeta {
  /** Key in the `settings` namespace. */
  labelKey: string;
  /** One line on the home card saying what the tab is for. */
  descriptionKey: string;
  icon: ReactNode;
}

export const SETTINGS_TAB_META: Readonly<Record<SettingsTab, TabMeta>> = {
  workshop: {
    labelKey: 'tabs.workshopIdentity',
    descriptionKey: 'groups.desc.workshopTab',
    icon: <StoreIcon />,
  },
  'workshop-pricing': {
    labelKey: 'tabs.workshopPricing',
    descriptionKey: 'groups.desc.workshopPricing',
    icon: <PaymentsIcon />,
  },
  'workshop-hours': {
    labelKey: 'tabs.workshopHours',
    descriptionKey: 'groups.desc.workshopHours',
    icon: <ScheduleIcon />,
  },
  notifications: {
    labelKey: 'notifications',
    descriptionKey: 'groups.desc.notifications',
    icon: <NotificationsIcon />,
  },
  modules: {
    labelKey: 'tabs.modules',
    descriptionKey: 'groups.desc.modules',
    icon: <ViewModuleIcon />,
  },
  'workorders-signature': {
    labelKey: 'tabs.workordersSignature',
    descriptionKey: 'groups.desc.workordersSignature',
    icon: <DriveFileRenameOutlineIcon />,
  },
  numbering: {
    labelKey: 'numbering.title',
    descriptionKey: 'groups.desc.numbering',
    icon: <NumbersIcon />,
  },
  services: {
    labelKey: 'tabs.services',
    descriptionKey: 'groups.desc.services',
    icon: <CategoryIcon />,
  },
  'inspection-templates': {
    labelKey: 'tabs.inspectionTemplates',
    descriptionKey: 'groups.desc.inspectionTemplates',
    icon: <FactCheckIcon />,
  },
  'checklist-templates': {
    labelKey: 'tabs.checklists',
    descriptionKey: 'groups.desc.checklists',
    icon: <PlaylistAddCheckIcon />,
  },
  'work-paths': {
    labelKey: 'tabs.workPaths',
    descriptionKey: 'groups.desc.workPaths',
    icon: <RouteIcon />,
  },
  automations: {
    labelKey: 'tabs.automations',
    descriptionKey: 'groups.desc.automations',
    icon: <AutoModeIcon />,
  },
  equipment: {
    labelKey: 'tabs.equipment',
    descriptionKey: 'groups.desc.equipment',
    icon: <HandymanIcon />,
  },
  'floor-plan': {
    labelKey: 'tabs.floorPlan',
    descriptionKey: 'groups.desc.floorPlan',
    icon: <MapIcon />,
  },
  parking: {
    labelKey: 'tabs.parking',
    descriptionKey: 'groups.desc.parking',
    icon: <LocalParkingIcon />,
  },
  'lift-status': {
    labelKey: 'tabs.liftStatus',
    descriptionKey: 'groups.desc.liftStatus',
    icon: <DashboardIcon />,
  },
  courtesy: {
    labelKey: 'tabs.courtesy',
    descriptionKey: 'groups.desc.courtesy',
    icon: <DirectionsCarIcon />,
  },
  branding: {
    labelKey: 'tabs.branding',
    descriptionKey: 'groups.desc.branding',
    icon: <PaletteIcon />,
  },
  whatsapp: {
    labelKey: 'tabs.whatsapp',
    descriptionKey: 'groups.desc.whatsapp',
    icon: <ChatIcon />,
  },
  svea: {
    labelKey: 'tabs.svea',
    descriptionKey: 'groups.desc.svea',
    icon: <CreditCardIcon />,
  },
  sumup: {
    labelKey: 'tabs.sumup',
    descriptionKey: 'groups.desc.sumup',
    icon: <PointOfSaleIcon />,
  },
  maventa: {
    labelKey: 'tabs.maventa',
    descriptionKey: 'groups.desc.maventa',
    icon: <ReceiptLongIcon />,
  },
  resurs: {
    labelKey: 'tabs.resurs',
    descriptionKey: 'groups.desc.resurs',
    icon: <AccountBalanceIcon />,
  },
  'inbound-email': {
    labelKey: 'tabs.inboundEmail',
    descriptionKey: 'groups.desc.inboundEmail',
    icon: <EmailOutlinedIcon />,
  },
  netvisor: {
    labelKey: 'tabs.netvisor',
    descriptionKey: 'groups.desc.netvisor',
    icon: <AccountBalanceIcon />,
  },
  sms: {
    labelKey: 'tabs.sms',
    descriptionKey: 'groups.desc.sms',
    icon: <SmsIcon />,
  },
  security: {
    labelKey: 'tabs.security',
    descriptionKey: 'groups.desc.security',
    icon: <LockClockIcon />,
  },
  'os-buddy': {
    labelKey: 'tabs.osBuddy',
    descriptionKey: 'groups.desc.osBuddy',
    icon: <SchoolIcon />,
  },
  'dashboard-defaults': {
    labelKey: 'tabs.dashboardDefaults',
    descriptionKey: 'groups.desc.dashboardDefaults',
    icon: <SpaceDashboardIcon />,
  },
  roles: {
    labelKey: 'tabs.rolesAndPermissions',
    descriptionKey: 'groups.desc.roles',
    icon: <AdminPanelSettingsIcon />,
  },
};

export const SETTINGS_GROUP_LABEL_KEY: Readonly<Record<SettingsGroup, string>> = {
  workshop: 'groups.workshop',
  work: 'groups.work',
  facilities: 'groups.facilities',
  system: 'groups.system',
};
